import {
  type EnrollPhoneNumber,
  PlanSNPType,
  PlanType,
  PlanCategoryType,
  type BasePlan,
  type EnrollFormData,
  GetBenefitsFrom,
  Sex,
  type IndividualRepresentative,
} from "../@types";
import type { SSN } from "../components/SsnInput";
import { isMapdPlanType, isPdPlanType } from "./planTypes";

export const emptyEnrollData: EnrollFormData = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  ethnicities: [],
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  individualRepresentative: {
    name: null,
    relationshipCode: null,
    nationalProducerNumber: null,
  } as IndividualRepresentative,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  races: [],
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const removeExtraWhiteSpace = (str: string): string =>
  str.replace(/\s+/g, " ").trim();

export const formatPhone = (
  phone: EnrollPhoneNumber,
  withDashes = true
): string => {
  const { areaCode, exchangeCode, lineNumber } = phone;
  const delimiter = withDashes ? "-" : "";

  return areaCode && exchangeCode && lineNumber
    ? [phone.areaCode, phone.exchangeCode, phone.lineNumber].join(delimiter)
    : "";
};

export const formatSSN = (ssn: SSN): string =>
  ssn.areaNumber && ssn.groupNumber && ssn.serialNumber
    ? `${ssn.areaNumber}${ssn.groupNumber}${ssn.serialNumber}`
    : "";

export const isCostPlanWithDrugs = (plan: BasePlan): boolean =>
  plan.plan_type !== PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isCostPlanWithoutDrugs = (plan: BasePlan): boolean =>
  plan.plan_type === PlanType.MA &&
  plan.category === PlanCategoryType.COST_1876;

export const isPffsMa = (plan: BasePlan): boolean =>
  plan.plan_type === PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const isPffsWithDrugs = (plan: BasePlan): boolean =>
  plan.plan_type !== PlanType.MA && plan.category === PlanCategoryType.PFFS;

export const shouldShowRaceEthnicitySection = (
  plan: BasePlan,
  tmpFeEnableEnrollmentRaceEthnicity: boolean
): boolean =>
  tmpFeEnableEnrollmentRaceEthnicity &&
  plan.category != PlanCategoryType.COST_1876;

export const shouldShowDrugCoverageQuestion = (plan: BasePlan): boolean => {
  const isMAPD = isMapdPlanType(plan.plan_type);
  const isPDP = isPdPlanType(plan.plan_type);
  const isCP_PD = isCostPlanWithDrugs(plan);
  const isPFFS_PD = isPffsWithDrugs(plan);
  const isSNP_DE = plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE;

  return isMAPD || isPDP || isSNP_DE || isPFFS_PD || isCP_PD;
};

export type EnrollmentPage =
  | "Sep"
  | "MedicareInfo"
  | "PersonalInfo"
  | "Address"
  | "GeneralQuestions"
  | "Paying"
  | "Agreement"
  | "TellUsMore"
  | "Review"
  | "Enrolled";

export const defaultStepPages: EnrollmentPage[] = [
  "MedicareInfo",
  "PersonalInfo",
  "Address",
  "GeneralQuestions",
  "Paying",
  "Agreement",
  "Review",
];

export const defaultOrderedPages: EnrollmentPage[] = [
  ...defaultStepPages,
  "Enrolled",
];

export const defaultIncludeSepOrderedPages: EnrollmentPage[] = [
  "Sep",
  ...defaultOrderedPages,
];

export const getEnrollmentPages = (
  includeSepPage: boolean
): { orderedPages: EnrollmentPage[]; stepPages: EnrollmentPage[] } => {
  // TODO: Rename these variables to be more intuitive.
  const orderedPages = includeSepPage
    ? defaultIncludeSepOrderedPages
    : defaultOrderedPages;

  return { orderedPages, stepPages: defaultStepPages };
};
